import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import {
  ArrowLeft,
  Briefcase,
  CheckCircle2,
  Eye,
  EyeOff,
  Lock,
  LogIn,
  Mail,
  ShieldCheck,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { signIn, requestPasswordReset } from "@/integrations/api/auth";
import { currentUserQueryOptions } from "@/hooks/useCurrentUser";

export const Route = createFileRoute("/auth")({
  ssr: false,
  validateSearch: z.object({ next: z.string().optional() }),
  component: AuthPage,
});

const MONO = { fontFamily: '"JetBrains Mono", ui-monospace, monospace' };

function AuthPage() {
  const { next } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<"signin" | "forgot">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [keepSignedIn, setKeepSignedIn] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    setErr(null);
    try {
      if (mode === "signin") {
        await signIn(email, password);
        const { getMe } = await import("@/integrations/api/auth");
        const { useAuthStore } = await import("@/store/useAuthStore");
        const me = await getMe();
        // Populate the shared QueryClient cache so subsequent route guards
        // (ensureQueryData) and AppShell (useQuery) read from cache — no extra request.
        queryClient.setQueryData(currentUserQueryOptions().queryKey, me);
        useAuthStore.getState().setAuth(me);

        // Check for forced password change BEFORE navigating to the app
        if (me.must_change_password) {
          navigate({ to: "/change-password" });
        } else if (next && next.startsWith("/")) {
          window.location.assign(next);
        } else {
          navigate({ to: "/" });
        }
      } else {
        await requestPasswordReset(email);
        setMsg("If an account exists, a password reset link has been sent.");
      }
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Something went wrong");
    } finally {
      setBusy(false);
    }
  }

  const isForgot = mode === "forgot";

  return (
    <div
      className="grid min-h-screen place-items-center bg-surface-soft px-4 py-10"
      style={{ fontFamily: '"Inter", ui-sans-serif, system-ui, sans-serif' }}
    >
      <div className="w-full max-w-6xl">
        <div className="grid overflow-hidden rounded-2xl border border-border bg-card shadow-[0_20px_60px_-20px_rgba(11,22,40,0.25)] lg:grid-cols-2">
          {/* LEFT PANEL */}
          <div className="relative hidden flex-col justify-between overflow-hidden bg-gradient-to-br from-secondary via-secondary to-[color:var(--surface-soft)] p-10 lg:flex">
            <div className="pointer-events-none absolute -top-24 -right-20 h-72 w-72 rounded-full bg-primary/5 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-24 -left-16 h-72 w-72 rounded-full bg-[color:var(--mint)]/15 blur-3xl" />

            <div className="relative">
              <div className="flex items-center gap-3">
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-primary text-primary-foreground shadow-sm">
                  <Briefcase className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-lg font-bold leading-tight text-foreground">HAMS</p>
                  <p className="text-xs text-muted-foreground">Hospital Asset Management System</p>
                </div>
              </div>

              <h2 className="mt-10 text-3xl font-semibold leading-tight tracking-tight text-foreground">
                Secure access for hospital{" "}
                <span className="text-[color:var(--mint-foreground)] underline decoration-[color:var(--mint)] decoration-4 underline-offset-4">
                  asset governance
                </span>
              </h2>
              <p className="mt-4 max-w-md text-sm leading-6 text-muted-foreground">
                Manage asset lifecycle, preventive maintenance, calibration, compliance, insurance and finance
                workflows from one controlled platform.
              </p>
            </div>

            <div className="relative mt-10 space-y-6">
              <div className="rounded-2xl border border-border bg-card/80 p-5 backdrop-blur">
                <div className="flex items-center gap-3">
                  <span className="grid h-9 w-9 place-items-center rounded-full bg-[color:var(--mint)]/25 text-[color:var(--mint-foreground)]">
                    <CheckCircle2 className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground" style={MONO}>
                      System Access
                    </p>
                    <p className="text-sm font-semibold text-foreground">Role-based access enabled</p>
                  </div>
                </div>
                <p className="mt-3 text-xs leading-5 text-muted-foreground">
                  Users are created and approved by the hospital administrator.
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {["Biomedical", "Finance", "Quality", "Facility"].map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center rounded-full border border-border bg-background px-2.5 py-1 text-[11px] font-medium text-foreground"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              <p className="text-[11px] uppercase tracking-wider text-muted-foreground" style={MONO}>
                Built for internal hospital teams
              </p>
            </div>
          </div>

          {/* RIGHT PANEL */}
          <div className="bg-card p-8 sm:p-10 lg:p-12">
            <div className="mx-auto w-full max-w-md">
              <h1 className="text-2xl font-semibold tracking-tight text-foreground">
                {isForgot ? "Reset your password" : "Hospital Login"}
              </h1>
              <p className="mt-1.5 text-sm text-muted-foreground">
                {isForgot
                  ? "Enter your email and we will send a password reset link."
                  : "Authorized hospital personnel only."}
              </p>

              <form className="mt-8 space-y-5" onSubmit={submit}>
                <div>
                  <label
                    htmlFor="email"
                    className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
                    style={MONO}
                  >
                    Email
                  </label>
                  <div className="relative mt-2">
                    <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      required
                      placeholder="name@hospital.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-12 w-full rounded-lg border border-border bg-background pl-10 pr-4 text-sm text-foreground outline-none transition focus:border-primary focus:ring-2 focus:ring-[color:var(--mint)]/40"
                    />
                  </div>
                </div>

                {!isForgot && (
                  <div>
                    <div className="flex items-center justify-between">
                      <label
                        htmlFor="password"
                        className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
                        style={MONO}
                      >
                        Password
                      </label>
                      <button
                        type="button"
                        onClick={() => { setMode("forgot"); setErr(null); setMsg(null); }}
                        className="text-[11px] font-semibold uppercase tracking-wider text-[color:var(--mint-foreground)] hover:underline"
                        style={MONO}
                      >
                        Forgot password?
                      </button>
                    </div>
                    <div className="relative mt-2">
                      <Lock className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        autoComplete="current-password"
                        required
                        minLength={6}
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="h-12 w-full rounded-lg border border-border bg-background pl-10 pr-11 text-sm text-foreground outline-none transition focus:border-primary focus:ring-2 focus:ring-[color:var(--mint)]/40"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-2 text-muted-foreground hover:bg-secondary hover:text-foreground"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                )}

                {!isForgot && (
                  <label className="flex cursor-pointer select-none items-center gap-2.5 text-sm text-foreground">
                    <input
                      type="checkbox"
                      checked={keepSignedIn}
                      onChange={(e) => setKeepSignedIn(e.target.checked)}
                      className="h-4 w-4 rounded border-border accent-[color:var(--primary)]"
                    />
                    Keep me signed in on this device
                  </label>
                )}

                {err && (
                  <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-sm text-destructive">
                    {err}
                  </div>
                )}
                {msg && (
                  <div className="rounded-lg border border-[color:var(--mint)]/40 bg-[color:var(--mint)]/15 px-3 py-2.5 text-sm text-[color:var(--mint-foreground)]">
                    {msg}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={busy}
                  className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-primary text-sm font-semibold text-primary-foreground shadow-sm transition hover:bg-primary/90 disabled:opacity-60"
                >
                  <LogIn className="h-4 w-4" />
                  {busy ? "Please wait…" : isForgot ? "Send reset link" : "Secure Sign in"}
                </button>

                <p className="flex items-center justify-center gap-2 text-[11px] uppercase tracking-wider text-muted-foreground" style={MONO}>
                  <ShieldCheck className="h-3.5 w-3.5 text-[color:var(--mint-foreground)]" />
                  Role-based access · Audit trail · Secure hospital data
                </p>

                {isForgot && (
                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => { setMode("signin"); setErr(null); setMsg(null); }}
                      className="text-sm font-medium text-primary hover:underline"
                    >
                      ← Back to sign in
                    </button>
                  </div>
                )}
              </form>

              <p className="mt-8 border-t border-border pt-5 text-center text-xs text-muted-foreground">
                Need access? Contact your hospital administrator.
              </p>
              {/* Temporarily hidden vendor login option */}
              {/* <p className="mt-3 text-center text-sm">
                <Link to="/vendor-login" className="font-medium text-primary hover:underline">
                  Are you a vendor? Sign in to the Vendor Portal →
                </Link>
              </p> */}
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-col items-center gap-3 text-center">
          <p className="text-xs uppercase tracking-wider text-muted-foreground" style={MONO}>
            Role-based access · Audit-ready · Secure hospital data
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
